import {
  type QueryKey,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { Icons } from '@/assets/icons/icons';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { ScrollArea } from '@/components/ui/ScrollArea';
import { REHYPE_PLUGINS } from '@/components/ui/smoothdown/plugins';
import { Tooltip, TooltipProvider } from '@/components/ui/Tooltip';
import { cn } from '@/lib/utils';

import {
  type AssistantFile,
  type CatalogueEntry,
} from './assistant-files-query';

interface TreeNode {
  name: string;
  path: string;
  kind: 'file' | 'directory';
  description: string;
  sizeBytes: number;
  children: TreeNode[];
}

function buildTree(entries: CatalogueEntry[], rootPath: string): TreeNode[] {
  const root: TreeNode[] = [];
  const rootPrefix = `${rootPath}/`;

  for (const entry of entries) {
    if (!entry.path.startsWith(rootPrefix)) {
      continue;
    }

    const relativePath = entry.path.slice(rootPrefix.length);
    if (!relativePath) {
      continue;
    }

    const parts = relativePath.split('/');
    let currentLevel = root;
    let currentPath = rootPath;

    for (let index = 0; index < parts.length; index += 1) {
      const part = parts[index];
      const isLast = index === parts.length - 1;
      currentPath = `${currentPath}/${part}`;

      let node = currentLevel.find((item) => item.name === part);
      if (!node) {
        node = {
          name: part,
          path: currentPath,
          kind: isLast ? entry.kind : 'directory',
          description: isLast && entry.kind === 'file' ? entry.description : '',
          sizeBytes: isLast && entry.kind === 'file' ? entry.size_bytes : 0,
          children: [],
        };
        currentLevel.push(node);
      } else if (isLast) {
        node.kind = entry.kind;
        node.description = entry.kind === 'file' ? entry.description : '';
        node.sizeBytes = entry.kind === 'file' ? entry.size_bytes : 0;
      }

      currentLevel = node.children;
    }
  }

  const sortNodes = (nodes: TreeNode[]): TreeNode[] =>
    [...nodes]
      .sort((a, b) => {
        if (a.kind !== b.kind) {
          return a.kind === 'directory' ? -1 : 1;
        }
        return a.name.localeCompare(b.name);
      })
      .map((node) => ({
        ...node,
        children: sortNodes(node.children),
      }));

  return sortNodes(root);
}

interface TreeNodeRowProps {
  node: TreeNode;
  depth: number;
  selectedPath: string | null;
  expandedPaths: Set<string>;
  onSelectFile: (path: string) => void;
  onToggleDirectory: (path: string) => void;
}

function TreeNodeRow({
  node,
  depth,
  selectedPath,
  expandedPaths,
  onSelectFile,
  onToggleDirectory,
}: TreeNodeRowProps) {
  const isDirectory = node.kind === 'directory';
  const isExpanded = isDirectory && expandedPaths.has(node.path);
  const isSelected = !isDirectory && selectedPath === node.path;

  const handleClick = () => {
    if (isDirectory) {
      onToggleDirectory(node.path);
      return;
    }
    onSelectFile(node.path);
  };

  const row = (
    <button
      type="button"
      onClick={handleClick}
      className={cn(
        'flex w-full items-center gap-1 rounded-md px-2 py-1 text-left text-sm',
        'hover:bg-secondary-hover focus-ring',
        isSelected && 'bg-secondary-hover font-medium'
      )}
      style={{ paddingLeft: `${depth * 12 + 8}px` }}
    >
      {isDirectory ? (
        isExpanded ? (
          <Icons.chevronDown className="h-3.5 w-3.5 shrink-0 text-foreground-muted" />
        ) : (
          <Icons.chevronRight className="h-3.5 w-3.5 shrink-0 text-foreground-muted" />
        )
      ) : (
        <span className="inline-block h-3.5 w-3.5 shrink-0" />
      )}
      {isDirectory ? (
        <Icons.folder className="h-3.5 w-3.5 shrink-0 text-foreground-muted" />
      ) : (
        <Icons.file className="h-3.5 w-3.5 shrink-0 text-foreground-muted" />
      )}
      <span className="truncate">{node.name}</span>
    </button>
  );

  return (
    <YStack>
      {!isDirectory && node.description ? (
        <Tooltip label={node.description} side="right">
          {row}
        </Tooltip>
      ) : (
        row
      )}
      {isDirectory && isExpanded && (
        <YStack>
          {node.children.map((child) => (
            <TreeNodeRow
              key={child.path}
              node={child}
              depth={depth + 1}
              selectedPath={selectedPath}
              expandedPaths={expandedPaths}
              onSelectFile={onSelectFile}
              onToggleDirectory={onToggleDirectory}
            />
          ))}
        </YStack>
      )}
    </YStack>
  );
}

/** A notes store to browse: its root directory and how to load its files */
export type AssistantFilesSource<
  TCatalogueData,
  TCatalogueKey extends QueryKey,
  TFileKey extends QueryKey,
> = {
  rootPath: string;
  catalogueQuery: UseQueryOptions<
    TCatalogueData,
    Error,
    CatalogueEntry[],
    TCatalogueKey
  >;
  getFileQuery: (
    path: string | null
  ) => UseQueryOptions<AssistantFile, Error, AssistantFile, TFileKey>;
};

interface AssistantFilesViewerProps<
  TCatalogueData,
  TCatalogueKey extends QueryKey,
  TFileKey extends QueryKey,
> {
  className?: string;
  panelHeightClassName?: string;
  source: AssistantFilesSource<TCatalogueData, TCatalogueKey, TFileKey>;
}

export function AssistantFilesViewer<
  TCatalogueData,
  TCatalogueKey extends QueryKey,
  TFileKey extends QueryKey,
>({
  className,
  panelHeightClassName = 'h-[20rem]',
  source,
}: AssistantFilesViewerProps<TCatalogueData, TCatalogueKey, TFileKey>) {
  const queryClient = useQueryClient();
  const [selectedPath, setSelectedPath] = useState<string | null>(null);
  const [expandedPaths, setExpandedPaths] = useState<Set<string>>(
    () => new Set([source.rootPath])
  );

  const {
    data: catalogue = [],
    isLoading: isCatalogueLoading,
    error: catalogueError,
  } = useQuery(source.catalogueQuery);

  const {
    data: selectedFile,
    isLoading: isFileLoading,
    error: fileError,
  } = useQuery(source.getFileQuery(selectedPath));

  const tree = useMemo(
    () => buildTree(catalogue, source.rootPath),
    [catalogue, source.rootPath]
  );

  const handleSelectFile = useCallback(
    (path: string) => {
      setSelectedPath(path);
      void queryClient.invalidateQueries({
        queryKey: source.getFileQuery(path).queryKey,
      });
    },
    [queryClient, source]
  );

  const handleToggleDirectory = useCallback((path: string) => {
    setSelectedPath(null);
    setExpandedPaths((prev) => {
      const next = new Set(prev);
      if (next.has(path)) {
        // Collapse: also prune any expanded descendants so re-opening this
        // directory later starts fresh instead of reviving prior state.
        const descendantPrefix = `${path}/`;
        for (const candidate of next) {
          if (candidate === path || candidate.startsWith(descendantPrefix)) {
            next.delete(candidate);
          }
        }
      } else {
        next.add(path);
      }
      return next;
    });
  }, []);

  const messageStyle = 'p-4 text-foreground-muted text-sm select-text';

  return (
    <TooltipProvider>
      <YStack className={cn('min-w-0 gap-2 w-full', className)}>
        <XStack
          className={cn(
            'min-w-0 overflow-hidden rounded-lg border border-border bg-surface',
            panelHeightClassName
          )}
        >
          <YStack className="w-2/5 min-w-0 border-r border-border">
            <ScrollArea className="h-full">
              <YStack className="p-2">
                {isCatalogueLoading ? (
                  <div className={messageStyle}>Loading files...</div>
                ) : catalogueError ? (
                  <div className={messageStyle}>
                    Could not load assistant files. Try closing and reopening
                    this section.
                  </div>
                ) : tree.length === 0 ? (
                  <div className={messageStyle}>
                    No extended memory files yet.
                  </div>
                ) : (
                  tree.map((node) => (
                    <TreeNodeRow
                      key={node.path}
                      node={node}
                      depth={0}
                      selectedPath={selectedPath}
                      expandedPaths={expandedPaths}
                      onSelectFile={handleSelectFile}
                      onToggleDirectory={handleToggleDirectory}
                    />
                  ))
                )}
              </YStack>
            </ScrollArea>
          </YStack>

          <YStack className="flex-1 min-w-0">
            {!selectedPath ? (
              <div className={messageStyle}>
                Select a file to view its contents.
              </div>
            ) : isFileLoading ? (
              <div className={messageStyle}>Loading file...</div>
            ) : fileError ? (
              <div className={messageStyle}>
                {fileError instanceof Error &&
                fileError.message === 'File not found'
                  ? 'This file is no longer available. Select another file or refresh the list.'
                  : 'Could not load this file. Select it again or refresh the file list.'}
              </div>
            ) : !selectedFile ? (
              <div className={messageStyle}>
                Could not load this file. Select it again or refresh the file
                list.
              </div>
            ) : !selectedFile.text ? (
              <div className={messageStyle}>File is empty.</div>
            ) : (
              <ScrollArea className="h-full">
                <div className="p-4 prose prose-bird prose-sm max-w-none select-text">
                  <ReactMarkdown
                    rehypePlugins={REHYPE_PLUGINS}
                    remarkPlugins={[remarkGfm]}
                  >
                    {selectedFile.text}
                  </ReactMarkdown>
                </div>
              </ScrollArea>
            )}
          </YStack>
        </XStack>
      </YStack>
    </TooltipProvider>
  );
}
